import { Team, Venue, KnockoutMatch } from '../types/tournament';
import { usePredictions } from '../context/PredictionContext';
import { formatMatchDate, formatMatchTime } from '../hooks/useBracketData';

// Format team source codes for display
function formatTeamSource(source: string): string {
  // Handle group position sources like "1A", "2B", "3ABCDF"
  if (source.match(/^[123][A-L]+$/)) {
    const position = source[0];
    const groups = source.slice(1);

    if (position === '1') {
      return `Winner ${groups}`;
    } else if (position === '2') {
      return `Runner-up ${groups}`;
    } else if (position === '3') {
      // 3rd place from multiple groups
      if (groups.length > 1) {
        return `3rd (${groups.split('').join('/')})`;
      }
      return `3rd ${groups}`;
    }
  }

  // Handle knockout winner sources like "W73", "W89"
  if (source.startsWith('W')) {
    return `Winner M${source.slice(1)}`;
  }

  // Handle loser sources like "L101"
  if (source.startsWith('L')) {
    return `Loser M${source.slice(1)}`;
  }

  return source;
}

interface MatchCardProps {
  match: KnockoutMatch;
  team1: Team | undefined;
  team2: Team | undefined;
  venue: Venue | undefined;
  onSelectWinner?: (teamId: string) => void;
}

export function MatchCard({ match, team1, team2, venue, onSelectWinner }: MatchCardProps) {
  const { getKnockoutWinner, setKnockoutPrediction } = usePredictions();
  const selectedWinner = getKnockoutWinner(match.id);

  const handleTeamClick = (teamId: string | undefined) => {
    if (!teamId) return;
    setKnockoutPrediction(match.id, teamId);
    onSelectWinner?.(teamId);
  };

  const isTeam1Selected = selectedWinner === team1?.id;
  const isTeam2Selected = selectedWinner === team2?.id;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 w-48 overflow-hidden">
      {/* Header with match info */}
      <div className="bg-fifa-blue text-white px-2 py-1 flex justify-between items-center text-xs">
        <span className="font-bold">M{match.matchNumber}</span>
        <span>{formatMatchDate(match.date)} {formatMatchTime(match.time)}</span>
        <span className="truncate max-w-16">{venue?.city || 'TBD'}</span>
      </div>

      {/* Team 1 */}
      <button
        onClick={() => handleTeamClick(team1?.id)}
        disabled={!team1}
        className={`w-full px-2 py-2 flex items-center gap-2 transition-colors border-b border-gray-100
          ${team1 ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'}
          ${isTeam1Selected ? 'bg-green-100 border-l-4 border-l-green-500' : ''}
        `}
      >
        <span className="text-lg">{team1?.flag || '🏳️'}</span>
        <span className={`flex-1 text-left text-sm font-medium ${!team1 ? 'text-gray-400 text-xs' : ''}`}>
          {team1?.code || formatTeamSource(match.team1Source)}
        </span>
        {isTeam1Selected && (
          <span className="text-green-600 text-xs">✓</span>
        )}
      </button>

      {/* Team 2 */}
      <button
        onClick={() => handleTeamClick(team2?.id)}
        disabled={!team2}
        className={`w-full px-2 py-2 flex items-center gap-2 transition-colors
          ${team2 ? 'hover:bg-blue-50 cursor-pointer' : 'cursor-default'}
          ${isTeam2Selected ? 'bg-green-100 border-l-4 border-l-green-500' : ''}
        `}
      >
        <span className="text-lg">{team2?.flag || '🏳️'}</span>
        <span className={`flex-1 text-left text-sm font-medium ${!team2 ? 'text-gray-400 text-xs' : ''}`}>
          {team2?.code || formatTeamSource(match.team2Source)}
        </span>
        {isTeam2Selected && (
          <span className="text-green-600 text-xs">✓</span>
        )}
      </button>
    </div>
  );
}

interface GroupMatchCardProps {
  matchNumber: number;
  date: string;
  team1: Team | undefined;
  team2: Team | undefined;
  venue: Venue | undefined;
}

export function GroupMatchCard({ matchNumber, date, team1, team2, venue }: GroupMatchCardProps) {
  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-2 text-sm">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>M{matchNumber}</span>
        <span>{formatMatchDate(date)}</span>
      </div>
      <div className="flex items-center gap-2">
        <span>{team1?.flag || '🏳️'}</span>
        <span className="font-medium">{team1?.code || 'TBD'}</span>
        <span className="text-gray-400">vs</span>
        <span className="font-medium">{team2?.code || 'TBD'}</span>
        <span>{team2?.flag || '🏳️'}</span>
      </div>
      <div className="text-xs text-gray-500 mt-1">{venue?.city || 'TBD'}</div>
    </div>
  );
}
